import { useEffect, useMemo, useState } from 'react'
import { Check, Plus, ShieldCheck, RefreshCw, Zap, Search, ChevronDown, X, HeartHandshake, ChevronRight } from 'lucide-react'
import { PageHeader } from '../shell/PageHeader'
import { siApple, siFitbit, siGarmin, siGoogle } from 'simple-icons'
import { CATALOG, type Provider, type Category } from '../data/catalog'
import { API_BASE } from '@/api/chat'
import { getToken } from '@/api/auth'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { ProgramsPage } from './ProgramsPage'

// Real brand logos where an open icon exists; everything else uses a polished monogram.
const LOGOS: Record<string, { path: string; hex: string }> = {
  'apple-watch': siApple,
  'apple-records': siApple,
  fitbit: siFitbit,
  garmin: siGarmin,
  'health-connect': siGoogle,
  whoop: { path: '', hex: '000000' },
}

export function IntegrationsPage({ onMenu, showMenu, userId = 'demo-user' }: { onMenu: () => void; showMenu: boolean; userId?: string }) {
  // Connected state seeded from the catalog; "connecting" via a mock OAuth modal.
  const [connected, setConnected] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {}
    CATALOG.forEach((c) => c.providers.forEach((p) => { if (p.connected) seed[p.id] = true }))
    return seed
  })
  const [pending, setPending] = useState<Provider | null>(null)
  const [banner, setBanner] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  // Categories collapsed by default to cut the scroll; open on demand.
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [programsOpen, setProgramsOpen] = useState(false)

  // After a real OAuth callback the backend redirects to /?connected=<provider>&count=<n>.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    const provider = q.get('connected')
    if (provider) {
      const labels: Record<string, string> = { 'google-calendar': 'Google Calendar', 'google-health': 'Google Health', whoop: 'WHOOP', withings: 'Withings' }
      setBanner(`${labels[provider] || provider} connected — SARA can now use it in your coaching.`)
      window.history.replaceState({}, '', window.location.pathname)
    }
    // Reflect real backend connection state (map backend provider keys → catalog card ids).
    const MAP: Record<string, string> = { withings: 'withings-bpm', 'google-calendar': 'google-calendar', 'google-health': 'google-health', whoop: 'whoop' }
    fetch(`${API_BASE}/v1/integrations/status?user_id=${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((s: { connected: string[] }) => {
        setConnected((c) => {
          const next = { ...c }
          ;(s.connected || []).forEach((p) => { if (MAP[p]) next[MAP[p]] = true })
          return next
        })
      })
      .catch(() => {})
  }, [userId])

  const connectedCount = useMemo(() => Object.values(connected).filter(Boolean).length, [connected])

  // The integrations SARA can connect for real, right now (vs the broader roadmap catalog).
  const live = useMemo(() => CATALOG.flatMap((c) => c.providers.filter((p) => p.real)), [])

  // Search across every provider (name, blurb, data, auth) — flattens categories while typing.
  const q = query.trim().toLowerCase()
  const matches = (p: Provider) =>
    !q || [p.name, p.blurb, p.auth, ...(p.data || [])].join(' ').toLowerCase().includes(q)
  const results = useMemo(
    () => CATALOG.map((c) => ({ cat: c, providers: c.providers.filter(matches) })).filter((r) => r.providers.length),
    [q],
  )

  const startConnect = (p: Provider) => {
    if (p.real && p.oauthProvider) {
      // Real OAuth — full-page redirect. Pass the JWT so the backend ties the connection to the
      // signed-in user (OAuth redirects can't carry an Authorization header).
      const t = getToken()
      const qs = t ? `?token=${encodeURIComponent(t)}` : `?user_id=${encodeURIComponent(userId)}`
      window.location.href = `${API_BASE}/v1/integrations/${p.oauthProvider}/authorize${qs}`
      return
    }
    setPending(p)
  }

  const authorize = (p: Provider) => {
    setConnected((s) => ({ ...s, [p.id]: true }))
    setPending(null)
  }

  return (
    <div className="flex h-full flex-1 min-w-0 flex-col bg-background">
      <PageHeader
        title="Integrations"
        subtitle={`${connectedCount} connected · ${live.length} live · ${CATALOG.reduce((n, c) => n + c.providers.length, 0)} sources`}
        onMenu={onMenu}
        showMenu={showMenu}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-5 space-y-5">
          {banner && (
            <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-success-light p-4">
              <Check className="size-5 shrink-0 text-success-text" />
              <p className="text-sm text-foreground">{banner}</p>
            </div>
          )}

          {/* Search */}
          <div className="sticky top-0 z-10 -mx-4 px-4 md:-mx-6 md:px-6 py-2 bg-background/85 backdrop-blur">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search devices, apps, records…"
                className="w-full rounded-xl border border-border bg-card py-2.5 pl-9 pr-9 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 hover:bg-muted" aria-label="Clear search">
                  <X className="size-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {q ? (
            /* ── Search results (flat) ── */
            results.length ? (
              results.map(({ cat, providers }) => (
                <section key={cat.id}>
                  <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cat.label}</h2>
                  <div className="grid gap-3">
                    {providers.map((p) => (
                      <ProviderCard key={p.id} p={p} connected={!!connected[p.id]} onConnect={() => startConnect(p)} />
                    ))}
                  </div>
                </section>
              ))
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">No sources match “{query}”.</p>
            )
          ) : (
            <>
              {/* ── Live integrations: connectable right now ── */}
              <section>
                <div className="mb-2 flex items-center gap-2">
                  <Zap className="size-4 text-info-text" />
                  <h2 className="text-sm font-semibold text-foreground">Connect now</h2>
                  <span className="rounded-full bg-info-light px-2 py-0.5 text-[10px] font-medium text-info-text">Live</span>
                </div>
                <div className="grid gap-3">
                  {live.map((p) => (
                    <ProviderCard key={p.id} p={p} connected={!!connected[p.id]} onConnect={() => startConnect(p)} highlight />
                  ))}
                </div>
              </section>

              <div className="flex items-start gap-3 rounded-xl border border-border-subtle bg-info-light p-4">
                <ShieldCheck className="size-5 shrink-0 text-info-text mt-0.5" />
                <p className="text-sm text-foreground">
                  Connect your records, devices and apps once. SARA continuously syncs them into one
                  living health record — encrypted at rest. You control every connection and can revoke access anytime.
                </p>
              </div>

              {/* Health programs marketplace — vetted partner care, not a device/app connection */}
              <button
                onClick={() => setProgramsOpen(true)}
                className="lift flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-success-light">
                  <HeartHandshake className="size-4.5 text-success-text" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground">Health programs</span>
                  <span className="block text-xs text-muted-foreground">Hypertension, diabetes, behavioral health, gym, lab work — from vetted partners</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>

              {/* ── Full catalog as collapsible categories ── */}
              <div className="space-y-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">All sources</h2>
                {CATALOG.map((cat) => (
                  <CategorySection
                    key={cat.id}
                    cat={cat}
                    open={!!open[cat.id]}
                    onToggle={() => setOpen((o) => ({ ...o, [cat.id]: !o[cat.id] }))}
                    connected={connected}
                    onConnect={startConnect}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {pending && <ConnectModal provider={pending} onCancel={() => setPending(null)} onAuthorize={() => authorize(pending)} />}
      {programsOpen && <ProgramsPage userId={userId} onClose={() => setProgramsOpen(false)} />}
    </div>
  )
}

function CategorySection({ cat, open, onToggle, connected, onConnect }: {
  cat: Category; open: boolean; onToggle: () => void
  connected: Record<string, boolean>; onConnect: (p: Provider) => void
}) {
  const Icon = cat.icon
  const nConnected = cat.providers.filter((p) => connected[p.id]).length
  return (
    <section className="rounded-xl border border-border bg-card">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-ring rounded-xl"
      >
        <Icon className="size-5 shrink-0 text-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{cat.label}</h3>
            {nConnected > 0 && (
              <span className="rounded-full bg-success-light px-1.5 py-0.5 text-[10px] font-medium text-success-text">{nConnected} connected</span>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground">{cat.hint}</p>
        </div>
        <span className="shrink-0 text-[11px] text-muted-foreground">{cat.providers.length}</span>
        <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="grid gap-3 px-3 pb-3">
          {cat.providers.map((p) => (
            <ProviderCard key={p.id} p={p} connected={!!connected[p.id]} onConnect={() => onConnect(p)} />
          ))}
        </div>
      )}
    </section>
  )
}

function ProviderMark({ id, name, accent, size = 36 }: { id: string; name: string; accent: string; size?: number }) {
  const logo = LOGOS[id]
  if (logo && logo.path) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-xl"
        style={{ width: size, height: size, backgroundColor: `#${logo.hex}` }}
      >
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
          <path d={logo.path} />
        </svg>
      </span>
    )
  }
  const initials = name.replace(/[^a-zA-Z0-9 ]/g, '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-xl font-bold text-white"
      style={{ width: size, height: size, backgroundColor: accent, fontSize: size * 0.36 }}
    >
      {initials}
    </span>
  )
}

function ProviderCard({ p, connected, onConnect, highlight }: { p: Provider; connected: boolean; onConnect: () => void; highlight?: boolean }) {
  return (
    <div className={cn('lift flex flex-col rounded-xl border bg-card p-4', highlight ? 'border-info-text/30' : 'border-border')}>
      <div className="flex items-start gap-3">
        <ProviderMark id={p.id} name={p.name} accent={p.accent} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{p.name}</h3>
            {p.real && !connected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-info-light px-1.5 py-0.5 text-[10px] font-medium text-info-text">
                <Zap className="size-3" /> Live
              </span>
            )}
            {connected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-success-light px-1.5 py-0.5 text-[10px] font-medium text-success-text">
                <Check className="size-3" /> Connected
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{p.blurb}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {p.data.map((d) => (
          <span key={d} className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{d}</span>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-[10px] text-muted-foreground truncate" title={p.note}>
          {p.auth}{p.note ? ` · ${p.note}` : ''}
        </span>
        {connected ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
            <RefreshCw className="size-3" /> {p.lastSync ?? 'just now'}
          </span>
        ) : (
          <Button size="sm" variant="basic" className="shrink-0 gap-1" onClick={onConnect}>
            <Plus className="size-3.5" /> Connect
          </Button>
        )}
      </div>
    </div>
  )
}

function ConnectModal({ provider, onCancel, onAuthorize }: { provider: Provider; onCancel: () => void; onAuthorize: () => void }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-overlay p-4" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-2xl bg-card p-6 shadow-lg animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <ProviderMark id={provider.id} name={provider.name} accent={provider.accent} size={32} />
          <h2 className="text-base font-semibold text-foreground">Connect {provider.name}</h2>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          You'll be redirected to {provider.name} to sign in securely via{' '}
          <span className="font-medium text-foreground">{provider.auth}</span>. SARA never sees your password.
        </p>
        <div className="mt-4 rounded-lg border border-border-subtle bg-background p-3">
          <div className="text-xs font-medium text-foreground mb-1.5">SARA is requesting access to:</div>
          <ul className="space-y-1">
            {provider.data.map((d) => (
              <li key={d} className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="size-3.5 text-success-text" /> {d}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" className="flex-1" onClick={onAuthorize}>Authorize</Button>
        </div>
        <p className="mt-3 text-center text-[10px] text-muted-foreground">Demo — no real account is connected.</p>
      </div>
    </div>
  )
}
